import { SpeakableSpeechMark } from '../tts.constants';
import { findSpeechMark } from './tts-create-speakable-content';

type ProcessedSpeechMark = SpeakableSpeechMark & {
  processed?: boolean;
  indexInHtmlBody?: number;
};

/**
 * In-place version of injectListNumbers from tts-create-speakable-content.
 * Uses removable .tts-list-number spans so cleanup can restore the DOM (original
 * modifies parsed HTML text nodes; we can't easily undo that on live DOM).
 */
function injectListNumbersInPlace(container: HTMLElement): void {
  function processList(ol: HTMLOListElement): void {
    let counter = 1;

    ol.querySelectorAll(':scope > li').forEach(li => {
      const number = counter;

      li.classList.add('tts-list-item');

      li.querySelectorAll(':scope > p').forEach(p => {
        const span = document.createElement('span');
        span.className = 'tts-list-number';
        span.textContent = number + ' ';
        p.insertBefore(span, p.firstChild);
      });

      const nestedOl = li.querySelector('ol');
      if (nestedOl) {
        processList(nestedOl as HTMLOListElement);
      }

      counter++;
    });
  }

  container.querySelectorAll('ol').forEach(ol => {
    processList(ol as HTMLOListElement);
  });
}

/**
 * Injects speakable word spans and list-number markers into the live DOM
 * of the container (instruction + options, etc.) so TTS can highlight
 * without swapping HTML. Mutates the container and speech marks in place.
 *
 * @param containerElement - Root element that wraps all speakable content (e.g. SubActivity)
 * @param speechMarks - Speech marks from the audio item (mutated with processed/indexInHtmlBody for pause/resume)
 */
export function ttsInjectSpeakableInPlace(
  containerElement: HTMLElement,
  speechMarks: SpeakableSpeechMark[]
): void {
  const hasPlayedSpeechMarks = speechMarks.some(s => s.played);
  const marks = speechMarks as ProcessedSpeechMark[];

  injectListNumbersInPlace(containerElement);

  let lastTime = 0;
  let wordIndex = 0;

  /**
   * Process a single text node: match character sequences to speech marks,
   * wrap matches in span with id tts-{time} and class tts-word, replace node with
   * fragment. Unmatched characters stay as text nodes. Same logic as tts-create-speakable-content.
   */
  function processTextNode(textNode: Text): void {
    const text = textNode.textContent || '';
    const fragment = document.createDocumentFragment();
    let i = 0;

    while (i < text.length) {
      let combinedText = text[i];
      let matchIndex = i;

      while (matchIndex < text.length) {
        const speechMark = findSpeechMark({
          speechMarks: marks,
          wordIndex,
          combinedText,
          lastTime,
          hasPlayedSpeechMarks,
        });

        if (speechMark) {
          const span = document.createElement('span');
          span.id = `tts-${speechMark.time}`;
          span.className = 'tts-word';
          span.textContent = combinedText;
          fragment.appendChild(span);

          speechMark.processed = true;
          speechMark.indexInHtmlBody = wordIndex;
          lastTime = speechMark.time;

          i = matchIndex + 1;
          wordIndex++;
          break;
        }

        matchIndex++;
        if (matchIndex < text.length) {
          combinedText += text[matchIndex];
        }
      }

      if (matchIndex === text.length) {
        fragment.appendChild(document.createTextNode(text[i]));
        i++;
      }
    }

    textNode.replaceWith(fragment);
  }

  // Use TreeWalker so every text node in the container is visited in document order,
  // including text inside nested elements (e.g. multiple choice option buttons).
  // Recursing only over containerElement.childNodes can miss or reorder text when
  // the DOM structure differs from the serialized innerHTML order used by the string-based path.
  const walker = document.createTreeWalker(
    containerElement,
    NodeFilter.SHOW_TEXT,
    null
  );
  const textNodes: Text[] = [];
  let n: Node | null;
  while ((n = walker.nextNode())) {
    textNodes.push(n as Text);
  }
  textNodes.forEach(processTextNode);
}

/**
 * Removes all TTS-injected nodes and classes from the container so the DOM
 * and styles return to their prior state after playback ends or is stopped.
 *
 * @param containerElement - Same root element passed to ttsInjectSpeakableInPlace
 */
export function ttsRemoveSpeakableInPlace(containerElement: HTMLElement): void {
  const wordSpans = Array.from(containerElement.querySelectorAll('.tts-word'));
  wordSpans.forEach(span => {
    const textNode = document.createTextNode(
      (span as HTMLElement).textContent ?? ''
    );
    span.replaceWith(textNode);
  });

  const listNumberSpans = Array.from(
    containerElement.querySelectorAll('.tts-list-number')
  );
  listNumberSpans.forEach(el => el.remove());

  containerElement.querySelectorAll('li.tts-list-item').forEach(li => {
    li.classList.remove('tts-list-item');
  });
}
